import { useEffect, useCallback, useRef } from 'react';
import { zoom, ZoomBehavior, zoomIdentity } from 'd3-zoom';
import { select } from 'd3-selection';
import 'd3-transition';
import type { RefObject } from 'react';

type UseDrawingZoomOptions = {
  containerRef: RefObject<HTMLDivElement | null>;
  svgRef: RefObject<SVGSVGElement | null>;
  sheetWidth: number;
  sheetHeight: number;
};

export function useDrawingZoom({
  containerRef: _containerRef,
  svgRef,
  sheetWidth,
  sheetHeight,
}: UseDrawingZoomOptions) {
  const zoomBehaviorRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  const fitToSheet = useCallback(
    (svg: SVGSVGElement, zoomBehavior: ZoomBehavior<SVGSVGElement, unknown>) => {
      const rect = svg.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const padFraction = 0.05;
      const availW = rect.width * (1 - padFraction * 2);
      const availH = rect.height * (1 - padFraction * 2);

      const scale = Math.min(availW / sheetWidth, availH / sheetHeight);
      const tx = (rect.width - sheetWidth * scale) / 2;
      const ty = (rect.height - sheetHeight * scale) / 2;

      select(svg)
        .transition()
        .duration(300)
        .call(zoomBehavior.transform, zoomIdentity.translate(tx, ty).scale(scale));
    },
    [sheetWidth, sheetHeight],
  );

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const svgSelection = select(svg);
    const contentGroup = svgSelection.select<SVGGElement>('g.sheet-content');
    if (contentGroup.empty()) return;

    const zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 10])
      .on('zoom', (event) => {
        contentGroup.attr('transform', event.transform.toString());
      });

    svgSelection.call(zoomBehavior);

    // Prevent zoom on dimension handles
    svgSelection.on('dblclick.zoom', null);

    zoomBehaviorRef.current = zoomBehavior;

    // Initial fit
    requestAnimationFrame(() => {
      fitToSheet(svg, zoomBehavior);
    });

    return () => {
      svgSelection.on('.zoom', null);
      zoomBehaviorRef.current = null;
    };
  }, [fitToSheet, svgRef]);

  const zoomToFit = useCallback(() => {
    const svg = svgRef.current;
    const zb = zoomBehaviorRef.current;
    if (!svg || !zb) return;
    fitToSheet(svg, zb);
  }, [svgRef, fitToSheet]);

  const zoomToView = useCallback(
    (viewRect: { x: number; y: number; w: number; h: number }) => {
      const svg = svgRef.current;
      const zb = zoomBehaviorRef.current;
      if (!svg || !zb) return;

      const rect = svg.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const padFraction = 0.08;
      const availW = rect.width * (1 - padFraction * 2);
      const availH = rect.height * (1 - padFraction * 2);

      const scale = Math.min(availW / viewRect.w, availH / viewRect.h);
      const tx = rect.width / 2 - (viewRect.x + viewRect.w / 2) * scale;
      const ty = rect.height / 2 - (viewRect.y + viewRect.h / 2) * scale;

      select(svg)
        .transition()
        .duration(400)
        .call(zb.transform, zoomIdentity.translate(tx, ty).scale(scale));
    },
    [svgRef],
  );

  return { zoomToFit, zoomToView };
}
